import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Heart, MessageCircle, LogOut, Plus, Menu, X, Bell } from 'lucide-react';
import API from '../services/api';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [nonLus, setNonLus] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [nonLuesNotifs, setNonLuesNotifs] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    if (!user) return;

    const chargerNonLus = async () => {
      try {
        const res = await API.get('/messages/non-lus');
        setNonLus(res.data.non_lus);
      } catch (err) {
        console.error(err);
      }
    };

    const chargerNotifications = async () => {
      try {
        const res = await API.get('/notifications/non-lues');
        setNonLuesNotifs(res.data.total);
      } catch (err) {
        console.error(err);
      }
    };

    chargerNonLus();
    chargerNotifications();

    const interval = setInterval(chargerNonLus, 30000);
    const interval2 = setInterval(chargerNotifications, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(interval2);
    };
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav style={{ background: '#0A0A0A', borderBottom: '1px solid #222', position: 'sticky', top: 0, zIndex: 50, paddingTop: 'env(safe-area-inset-top)' }} className="text-white shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between" style={{ height: '52px' }}>

        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src="/logo.jpg" alt="JJ Cars" className="h-10 w-auto object-contain" />
        </Link>

        {/* Navigation desktop */}
        <div className="hidden md:flex items-center gap-5">
          <Link to="/annonces" className="text-gray-300 hover:text-yellow-400 transition font-medium text-sm">
            Annonces
          </Link>
          <Link to="/locations" className="text-gray-300 hover:text-yellow-400 transition font-medium text-sm">
            Locations
          </Link>
          <Link to="/abonnements" className="text-gray-300 hover:text-yellow-400 transition font-medium text-sm">
            Abonnements
          </Link>
          <Link to="/contact" className="text-gray-300 hover:text-yellow-400 transition font-medium text-sm">
            Contact
          </Link>

          {user ? (
            <>
              <Link
                to="/creer-annonce"
                style={{ background: '#F5A623' }}
                className="flex items-center gap-1 text-black px-3 py-1.5 rounded-full font-bold hover:opacity-90 transition text-sm"
              >
                <Plus size={14} /> Publier
              </Link>

              <Link to="/mes-favoris" className="text-gray-300 hover:text-yellow-400 transition">
                <Heart size={18} />
              </Link>

              <Link to="/messages" className="text-gray-300 hover:text-yellow-400 transition relative">
                <MessageCircle size={18} />
                {nonLus > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {nonLus > 9 ? '9+' : nonLus}
                  </span>
                )}
              </Link>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={async () => {
                    setShowNotifs(!showNotifs);
                    if (!showNotifs) {
                      const res = await API.get('/notifications');
                      setNotifications(res.data.notifications);
                    }
                  }}
                  className="text-gray-300 hover:text-yellow-400 transition relative"
                >
                  <Bell size={18} />
                  {nonLuesNotifs > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                      {nonLuesNotifs > 9 ? '9+' : nonLuesNotifs}
                    </span>
                  )}
                </button>

                {/* Dropdown notifications */}
                {showNotifs && (
                  <div style={{ background: '#111', border: '1px solid #333', width: '320px' }}
                    className="absolute right-0 top-8 rounded-2xl shadow-2xl z-50 overflow-hidden">
                    <div style={{ borderBottom: '1px solid #333' }} className="px-4 py-3 flex justify-between items-center">
                      <p className="text-white font-bold text-sm">Notifications</p>
                      <button
                        onClick={async () => {
                          await API.put('/notifications/toutes-lues');
                          setNonLuesNotifs(0);
                          setNotifications(notifications.map(n => ({ ...n, lu: true })));
                        }}
                        style={{ color: '#F5A623' }}
                        className="text-xs font-semibold hover:opacity-80"
                      >
                        Tout marquer lu
                      </button>
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 text-sm">
                          Aucune notification
                        </div>
                      ) : (
                        notifications.map(notif => (
                          <div
                            key={notif.id}
                            onClick={async () => {
                              await API.put(`/notifications/${notif.id}/lue`);
                              if (notif.lien) window.location.href = notif.lien;
                              setShowNotifs(false);
                            }}
                            style={{
                              background: notif.lu ? 'transparent' : '#1a1a1a',
                              borderBottom: '1px solid #222',
                              cursor: 'pointer'
                            }}
                            className="px-4 py-3 hover:bg-gray-800 transition"
                          >
                            <div className="flex items-start gap-2">
                              <div style={{ background: '#F5A62320' }} className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Bell size={14} style={{ color: '#F5A623' }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-xs font-bold">{notif.titre}</p>
                                <p className="text-gray-400 text-xs mt-0.5">{notif.message}</p>
                                <p className="text-gray-600 text-xs mt-1">
                                  {new Date(notif.date_creation).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                              {!notif.lu && (
                                <div style={{ background: '#F5A623' }} className="w-2 h-2 rounded-full flex-shrink-0 mt-1" />
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div style={{ borderTop: '1px solid #333' }} className="px-4 py-2 text-center">
                      <button
                        onClick={() => { navigate('/alertes'); setShowNotifs(false); }}
                        style={{ color: '#F5A623' }}
                        className="text-xs font-semibold hover:opacity-80"
                      >
                        Gérer mes alertes →
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  style={{ background: '#F5A623' }}
                  className="text-black px-3 py-1 rounded text-sm font-bold hover:opacity-90 transition"
                >
                  Admin
                </Link>
              )}

              <Link to="/profil" className="flex items-center gap-2 text-gray-300 hover:text-yellow-400 transition">
                <div style={{ background: '#F5A623' }} className="w-7 h-7 rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-xs">{user.prenom?.[0]}{user.nom?.[0]}</span>
                </div>
                <span className="font-medium text-sm">{user.prenom}</span>
              </Link>

              <button onClick={handleLogout} className="text-gray-400 hover:text-red-400 transition">
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <>
              <Link to="/connexion" className="text-gray-300 hover:text-yellow-400 transition font-medium text-sm">
                Connexion
              </Link>
              <Link
                to="/inscription"
                style={{ background: '#F5A623' }}
                className="text-black px-3 py-1.5 rounded-full font-bold hover:opacity-90 transition text-sm"
              >
                S'inscrire
              </Link>
            </>
          )}
        </div>

        {/* Actions mobile */}
        <div className="md:hidden flex items-center gap-4">
          {user && (
            <div className="relative">
              <button
                onClick={async () => {
                  setShowNotifs(!showNotifs);
                  if (!showNotifs) {
                    const res = await API.get('/notifications');
                    setNotifications(res.data.notifications);
                  }
                }}
                className="text-gray-300 hover:text-yellow-400 transition relative"
              >
                <Bell size={20} />
                {nonLuesNotifs > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {nonLuesNotifs > 9 ? '9+' : nonLuesNotifs}
                  </span>
                )}
              </button>

              {showNotifs && (
                <div style={{ background: '#111', border: '1px solid #333', width: '280px' }}
                  className="absolute right-0 top-8 rounded-2xl shadow-2xl z-50 overflow-hidden">
                  <div style={{ borderBottom: '1px solid #333' }} className="px-4 py-3 flex justify-between items-center">
                    <p className="text-white font-bold text-sm">Notifications</p>
                    <button
                      onClick={async () => {
                        await API.put('/notifications/toutes-lues');
                        setNonLuesNotifs(0);
                        setNotifications(notifications.map(n => ({ ...n, lu: true })));
                      }}
                      style={{ color: '#F5A623' }}
                      className="text-xs font-semibold hover:opacity-80"
                    >
                      Tout marquer lu
                    </button>
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        Aucune notification
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif.id}
                          onClick={async () => {
                            await API.put(`/notifications/${notif.id}/lue`);
                            if (notif.lien) window.location.href = notif.lien;
                            setShowNotifs(false);
                          }}
                          style={{
                            background: notif.lu ? 'transparent' : '#1a1a1a',
                            borderBottom: '1px solid #222',
                            cursor: 'pointer'
                          }}
                          className="px-4 py-3 hover:bg-gray-800 transition"
                        >
                          <div className="flex items-start gap-2">
                            <div style={{ background: '#F5A62320' }} className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Bell size={14} style={{ color: '#F5A623' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-bold">{notif.titre}</p>
                              <p className="text-gray-400 text-xs mt-0.5">{notif.message}</p>
                              <p className="text-gray-600 text-xs mt-1">
                                {new Date(notif.date_creation).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            {!notif.lu && (
                              <div style={{ background: '#F5A623' }} className="w-2 h-2 rounded-full flex-shrink-0 mt-1" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div style={{ borderTop: '1px solid #333' }} className="px-4 py-2 text-center">
                    <button
                      onClick={() => { navigate('/alertes'); setShowNotifs(false); }}
                      style={{ color: '#F5A623' }}
                      className="text-xs font-semibold hover:opacity-80"
                    >
                      Gérer mes alertes →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            className="text-gray-300"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Menu mobile ouvert */}
      {menuOpen && (
        <div style={{ background: '#111' }} className="md:hidden px-4 py-4 space-y-4 border-t border-gray-800">
          <Link to="/annonces" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Annonces</Link>
          <Link to="/locations" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Locations</Link>
          <Link to="/abonnements" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Abonnements</Link>
          <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Contact</Link>
          {user ? (
            <>
              <Link to="/creer-annonce" onClick={() => setMenuOpen(false)} className="block text-yellow-400 font-bold">+ Publier une annonce</Link>
              <Link to="/mes-favoris" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Mes favoris</Link>
              <Link to="/messages" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Messages {nonLus > 0 && `(${nonLus})`}</Link>
              <Link to="/alertes" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Mes alertes</Link>
              <Link to="/profil" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Mon profil</Link>
              {user.role === 'admin' && <Link to="/admin" onClick={() => setMenuOpen(false)} className="block text-yellow-400 font-bold">Admin</Link>}
              <button onClick={handleLogout} className="block text-red-400">Se déconnecter</button>
            </>
          ) : (
            <>
              <Link to="/connexion" onClick={() => setMenuOpen(false)} className="block text-gray-300 hover:text-yellow-400">Connexion</Link>
              <Link to="/inscription" onClick={() => setMenuOpen(false)} className="block text-yellow-400 font-bold">S'inscrire</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;